import { Box, Button, Chip, Tooltip } from '@mui/material';
import { ChatBubbleOutline } from '@mui/icons-material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import {
    PaloColumn,
    PaloFilterChips,
    PaloListView,
    PaloLockedSwitch,
    paloLockedHint,
    usePaloLockedRecords,
} from '@synergy/components-palo';
import { ReactElement, useEffect } from 'react';
import { tokens } from '@synergy/theme-palo';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import urljoin from 'url-join';

import * as CustomerListActions from '../customer-list/store/customer-list-actions';
import * as CustomerListSelectors from '../customer-list/store/customer-list-selectors';
import { CustomerListItem } from '../customer-list/models/customer-list-item';
import { PaloContactName } from '../../../../common/components/palo/palo-contact-card';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { PaloListLogo } from '../../../../common/components/palo/palo-list-logo';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListSelection } from '../../../../common/components/palo/palo-list-selection';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloLockedCount } from '../../../../common/components/palo/use-palo-locked-count';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const addressOf = (customer: CustomerListItem) =>
    customer.mainAddress || customer.addresses?.find(address => address.isMainAddress) || customer.addresses?.[0];

export const PaloCustomerList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('customers');
    const columnFilter = usePaloColumnFilters('customers');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    // Loaded once by the app for every picker; the filter only reads it.
    const { sortedByName: staff } = useAppSelector(state => state.contacts);
    const { sort, params } = usePaloSort('name', 'asc');
    // Locked partners are out of the list unless they are asked for
    // (LST-S14); the switch that asks for them sits in the filter row.
    const locked = usePaloLockedRecords(params);
    const apiBaseUrl = useAppSelector(state => state.settings.apiBaseUrl);
    const imageBaseUrl = useAppSelector(state => state.settings.imageCachePaths?.['96x48']);
    const requesting = useAppSelector(CustomerListSelectors.customerListRequestingSelector);
    const paginated = useAppSelector(CustomerListSelectors.customerListItemsSelector);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);
    const imageBase = apiBaseUrl && imageBaseUrl ? urljoin(apiBaseUrl, imageBaseUrl) : undefined;

    useEffect(() => {
        dispatch(CustomerListActions.fetchCustomerList(locked.params));
    }, [dispatch, locked.params]);

    const lockedMatches = usePaloLockedCount(
        '/customers',
        locked.params,
        !locked.includeLocked && !requesting && 0 === paginated.items.length
    );

    // Picking several and locking them in one go — the same bar on all three
    // master data lists (#2212).
    const { selection, actions: selectionActions } = usePaloListSelection(
        paginated.items,
        id =>
            dispatch((_d, _g, { api }) => api.contactsAndOrganizations.Customer.CustomerDetail.lockCustomer(id)).then(
                result => result.success
            ),
        id =>
            dispatch((_d, _g, { api }) => api.contactsAndOrganizations.Customer.CustomerDetail.unlockCustomer(id)).then(
                result => result.success
            ),
        () => dispatch(CustomerListActions.fetchCustomerList(locked.params))
    );

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const detailHref = (customer: CustomerListItem): string => location.pathname + '/' + customer.id;

    const columns: PaloColumn<CustomerListItem>[] = [
        {
            key: 'logo',
            type: 'icon',
            head: '',
            cell: customer => (
                <PaloListLogo
                    name={customer.name}
                    src={customer.image && imageBase ? urljoin(imageBase, customer.image) : undefined}
                    locked={customer.isLocked}
                />
            ),
        },
        {
            key: 'number',
            link: true,
            type: 'documentNumber',
            sortKey: 'number',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: customer => customer.number ?? customer.id,
        },
        {
            key: 'name',
            type: 'name',
            sortKey: 'name',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: customer => <PaloContactName company={customer} kind="customer" />,
        },
        {
            // Only what the account records. "ausgetreten" and "in Aufnahme"
            // live in the company name today and have no field of their own,
            // so they are not offered here.
            key: 'status',
            sortKey: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: customer => (
                <Chip
                    size="small"
                    color={customer.isLocked ? 'error' : 'success'}
                    label={<FormattedMessage id={customer.isLocked ? 'app.status.locked' : 'app.status.active'} />}
                />
            ),
        },
        {
            key: 'memberSince',
            sortKey: 'memberSince',
            type: 'date',
            head: <FormattedMessage id="app.table.column.memberSince" />,
            cell: customer =>
                customer.memberSince ? (
                    <FormattedDate value={customer.memberSince} day="2-digit" month="2-digit" year="numeric" />
                ) : null,
        },
        {
            key: 'responsiblePerson',
            sortKey: 'responsiblePerson',
            type: 'name',
            head: <FormattedMessage id="app.table.column.responsiblePerson" />,
            quickFilter: {
                key: 'responsiblePersonIds',
                options: staff.map(contact => ({ value: String(contact.id), label: contact.fullName })),
                value: searchParams.get('responsiblePersonIds') ?? '',
                onChange: value => {
                    const next = new URLSearchParams(searchParams);

                    if (value) {
                        next.set('responsiblePersonIds', value);
                    } else {
                        next.delete('responsiblePersonIds');
                    }

                    next.set('page', '1');
                    setSearchParams(next);
                },
            },
            cell: customer => customer.responsiblePersonName,
        },
        {
            key: 'city',
            sortKey: 'city',
            type: 'name',
            head: <FormattedMessage id="app.table.column.cityAndCountry" />,
            // Typed, not picked: as on the supplier list, a list of every
            // country a member could sit in helps nobody in a column this
            // narrow (#2194).
            quickFilter: {
                key: 'countries',
                placeholder: 'AT',
                value: searchParams.get('countries') ?? '',
                onChange: value => {
                    const next = new URLSearchParams(searchParams);

                    if (value) {
                        next.set('countries', value);
                    } else {
                        next.delete('countries');
                    }

                    next.set('page', '1');
                    setSearchParams(next);
                },
            },
            cell: customer => {
                const address = addressOf(customer);

                return [address?.city, address?.countryCode ?? address?.country].filter(Boolean).join(', ');
            },
        },
        {
            key: 'group',
            sortKey: 'group',
            type: 'name',
            head: <FormattedMessage id="app.table.column.companyGroup" />,
            cell: customer => customer.parentName,
        },
        {
            // The note itself can run to a paragraph, so the row shows that one
            // exists and hands over the text on hover.
            key: 'note',
            width: '40px',
            align: 'center',
            head: <FormattedMessage id="app.table.column.note" />,
            cell: customer =>
                customer.internalNotes?.trim() ? (
                    <Tooltip title={customer.internalNotes}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', color: tokens.color.ink3 }}>
                            <ChatBubbleOutline sx={{ fontSize: 16 }} />
                        </Box>
                    </Tooltip>
                ) : null,
        },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(ROUTES.contactsAndOrganizations.customers.new)}
        >
            <FormattedMessage id="app.common.newCustomer" />
        </Button>
    );

    return (
        <PaloListView<CustomerListItem>
            filterChips={
                <PaloFilterChips
                    names={{
                        responsiblePersonIds: staff.map(contact => ({
                            value: String(contact.id),
                            label: contact.fullName,
                        })),
                    }}
                />
            }
            filterToggle={<PaloLockedSwitch checked={locked.includeLocked} onChange={locked.setIncludeLocked} />}
            emptyHint={
                lockedMatches > 0 ? (
                    <Box
                        component="span"
                        role="button"
                        onClick={() => locked.setIncludeLocked(true)}
                        sx={{ color: tokens.color.accent, cursor: 'pointer', textDecoration: 'underline' }}
                    >
                        {paloLockedHint(lockedMatches)}
                    </Box>
                ) : undefined
            }
            selection={selection}
            selectionActions={selectionActions}
            title={<FormattedMessage id="app.common.customers" />}
            pageTitle={intl.formatMessage({ id: 'app.common.customers' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            searchValue={searchParams.get('search') ?? ''}
            onSearchChange={value => {
                const next = new URLSearchParams(searchParams);
                if (value) {
                    next.set('search', value);
                } else {
                    next.delete('search');
                }
                next.set('page', '1');
                setSearchParams(next, { replace: true });
            }}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="customers" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={customer => customer.id}
            requesting={requesting}
            emptyLabel="Keine Mitglieder."
            rowHref={detailHref}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        />
    );
};
